import React, { useState, useEffect } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { Switch } from '../components/ui/switch';
import { MoneyInput } from '../components/MoneyInput';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { CalendarIcon, CheckCircle, AlertCircle, ClipboardList, DollarSign, Loader2, Phone } from 'lucide-react';

const IntInput = ({ label, name, value, onChange, required, helper }) => (
  <div className="space-y-1">
    <Label className="text-slate-700 text-sm font-medium">
      {label} {required && <span className="text-red-500">*</span>}
    </Label>
    <Input type="number" min="0"
      value={value === '' || value === null || value === undefined ? '' : value}
      onChange={e => onChange(name, e.target.value === '' ? '' : parseInt(e.target.value) || 0)}
      className="bg-white" />
    {helper && <p className="text-xs text-slate-400">{helper}</p>}
  </div>
);

export default function PublicFormsPage() {
  const { token } = useParams();
  const location = useLocation();
  const [orgInfo, setOrgInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Auto-detect form from URL path
  const pathForm = location.pathname.endsWith('/eod') ? 'eod'
    : location.pathname.endsWith('/callcenter') ? 'callcenter'
    : location.pathname.endsWith('/sale') ? 'sale'
    : null;
  const [activeForm, setActiveForm] = useState(pathForm); // null = landing

  // EOD state
  const [eodForm, setEodForm] = useState({
    submission_date: format(new Date(), 'yyyy-MM-dd'), location_id: '',
    inbound_calls: '', calls_answered: '', appointments_scheduled: '',
    total_consults_scheduled: '', total_consults_showed: '',
    full_arch_cases_presented: '', non_full_arch_cases_presented: '',
    closed_today_from_today: '', full_arch_sold_today_from_today: '',
    arches_sold_today_from_today: '', total_treatments_sold_today_from_today: '',
    closed_from_previous: '', full_arch_sold_from_previous: '',
    arches_sold_from_previous: '', non_full_arch_from_previous: '',
    total_treatments_sold_from_previous: '', total_collections_today: '', surgical_production_today: '',
    notes: ''
  });
  const [eodDateOpen, setEodDateOpen] = useState(false);

  // Sale state
  const [saleForm, setSaleForm] = useState({
    submission_datetime: new Date().toISOString(), location_id: '',
    patient_first_name: '', patient_last_name: '',
    initial_consult_date: format(new Date(), 'yyyy-MM-dd'),
    treatment_sold: '', total_sales_amount: '', total_collected_today: '',
    payment_method: '', financing_amount: '', other_funds_amount: '',
    anticipated_paid_in_full_date: '', treatment_start_date: '',
    referral_source: '', will_use_dental_insurance: false, notes: ''
  });
  const [calStates, setCalStates] = useState({});

  useEffect(() => {
    axios.get(`${API}/public/forms/${token}/info`)
      .then(r => setOrgInfo(r.data))
      .catch(e => setError(e?.response?.data?.detail || 'Form not found'))
      .finally(() => setLoading(false));
  }, [token]);

  const setEodField = (name, val) => setEodForm(p => ({ ...p, [name]: val }));
  const setSaleField = (name, val) => setSaleForm(p => ({ ...p, [name]: val }));
  const setCal = (name, open) => setCalStates(p => ({ ...p, [name]: open }));

  const handleEodSubmit = async (e) => {
    e.preventDefault();
    if (!eodForm.location_id) { toast.error('Please select a location'); return; }
    setSubmitting(true);
    try {
      const payload = {
        ...eodForm,
        inbound_calls: parseInt(eodForm.inbound_calls) || 0,
        calls_answered: parseInt(eodForm.calls_answered) || 0,
        appointments_scheduled: parseInt(eodForm.appointments_scheduled) || 0,
        total_consults_scheduled: parseInt(eodForm.total_consults_scheduled) || 0,
        total_consults_showed: parseInt(eodForm.total_consults_showed) || 0,
        full_arch_cases_presented: parseInt(eodForm.full_arch_cases_presented) || 0,
        non_full_arch_cases_presented: parseInt(eodForm.non_full_arch_cases_presented) || 0,
        closed_today_from_today: parseInt(eodForm.closed_today_from_today) || 0,
        full_arch_sold_today_from_today: parseInt(eodForm.full_arch_sold_today_from_today) || 0,
        arches_sold_today_from_today: parseInt(eodForm.arches_sold_today_from_today) || 0,
        total_treatments_sold_today_from_today: parseInt(eodForm.total_treatments_sold_today_from_today) || 0,
        closed_from_previous: parseInt(eodForm.closed_from_previous) || 0,
        full_arch_sold_from_previous: parseInt(eodForm.full_arch_sold_from_previous) || 0,
        arches_sold_from_previous: parseInt(eodForm.arches_sold_from_previous) || 0,
        non_full_arch_from_previous: parseInt(eodForm.non_full_arch_from_previous) || 0,
        total_treatments_sold_from_previous: parseInt(eodForm.total_treatments_sold_from_previous) || 0,
        total_collections_today: parseFloat(eodForm.total_collections_today) || 0,
        surgical_production_today: parseFloat(eodForm.surgical_production_today) || 0,
      };
      await axios.post(`${API}/public/forms/${token}/eod`, payload);
      setSubmitted(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Submission failed');
    } finally { setSubmitting(false); }
  };

  const handleSaleSubmit = async (e) => {
    e.preventDefault();
    if (!saleForm.location_id || !saleForm.patient_first_name || !saleForm.patient_last_name
      || !saleForm.treatment_sold || !saleForm.payment_method || !saleForm.referral_source) {
      toast.error('Please fill all required fields');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...saleForm,
        total_sales_amount: parseFloat(saleForm.total_sales_amount) || 0,
        total_collected_today: parseFloat(saleForm.total_collected_today) || 0,
        financing_amount: parseFloat(saleForm.financing_amount) || 0,
        other_funds_amount: parseFloat(saleForm.other_funds_amount) || 0,
        anticipated_paid_in_full_date: saleForm.anticipated_paid_in_full_date || null,
        treatment_start_date: saleForm.treatment_start_date || null,
      };
      await axios.post(`${API}/public/forms/${token}/sale`, payload);
      setSubmitted(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Submission failed');
    } finally { setSubmitting(false); }
  };

  const DatePickerField = ({ label, fieldName, value, onChange }) => (
    <div className="space-y-1">
      <Label className="text-slate-700 text-sm font-medium">{label}</Label>
      <Popover open={calStates[fieldName]} onOpenChange={o => setCal(fieldName, o)}>
        <PopoverTrigger asChild>
          <Button variant="outline" className="w-full justify-start text-left text-sm h-9">
            <CalendarIcon size={13} className="mr-2" />
            {value ? format(new Date(value + 'T00:00:00'), 'MMM d, yyyy') : 'Select date'}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 bg-white">
          <Calendar mode="single"
            selected={value ? new Date(value + 'T00:00:00') : undefined}
            onSelect={d => { if (d) { onChange(fieldName, format(d, 'yyyy-MM-dd')); setCal(fieldName, false); } }} />
        </PopoverContent>
      </Popover>
    </div>
  );

  if (loading) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <Loader2 size={32} className="text-cyan-500 animate-spin" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="text-center">
        <AlertCircle size={40} className="text-red-400 mx-auto mb-3" />
        <h2 className="text-xl font-semibold text-slate-800 mb-2">Form not available</h2>
        <p className="text-slate-500">{error}</p>
      </div>
    </div>
  );

  if (submitted) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="text-center max-w-sm">
        <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
          <CheckCircle size={32} className="text-green-600" />
        </div>
        <h2 className="text-xl font-semibold text-slate-800 mb-2">Submitted successfully!</h2>
        <p className="text-slate-500 mb-6">Thank you. Your {activeForm === 'eod' ? 'End of Day (Sales)' : activeForm === 'callcenter' ? 'Call Center EOD' : 'sale'} record has been recorded.</p>
        <Button onClick={() => { setSubmitted(false); setActiveForm(null); }}
          style={{ background: 'hsl(191,95%,43%)', color: 'hsl(222,47%,11%)' }}>
          Submit Another
        </Button>
      </div>
    </div>
  );

  // Landing — pick form type
  if (!activeForm) return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="max-w-lg mx-auto flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'hsl(191,95%,43%)' }}>
            <span className="font-bold text-slate-900 text-xs">FA</span>
          </div>
          <div>
            <p className="font-semibold text-slate-800 text-sm">{orgInfo?.org_name}</p>
            <p className="text-slate-400 text-xs">Full Arch ROI Dashboard</p>
          </div>
        </div>
      </div>
      <div className="max-w-2xl mx-auto px-6 py-12">
        <h1 className="text-2xl font-bold text-slate-800 mb-2 font-heading">Staff Data Entry</h1>
        <p className="text-slate-500 mb-8">Select the form you need to fill out.</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <button onClick={() => setActiveForm('eod')}
            className="p-6 bg-white rounded-xl border-2 border-slate-200 hover:border-blue-400 hover:shadow-md text-left transition-all group">
            <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center mb-3 group-hover:bg-blue-600 transition-colors">
              <ClipboardList size={20} className="text-blue-600 group-hover:text-white transition-colors" />
            </div>
            <h3 className="font-semibold text-slate-800 mb-1">EOD Summary</h3>
            <p className="text-slate-500 text-sm">Sales & consult outcomes</p>
          </button>
          <button onClick={() => setActiveForm('callcenter')}
            className="p-6 bg-white rounded-xl border-2 border-slate-200 hover:border-blue-400 hover:shadow-md text-left transition-all group">
            <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center mb-3 group-hover:bg-purple-600 transition-colors">
              <Phone size={20} className="text-purple-600 group-hover:text-white transition-colors" />
            </div>
            <h3 className="font-semibold text-slate-800 mb-1">Call Center EOD</h3>
            <p className="text-slate-500 text-sm">Phone & scheduling metrics</p>
          </button>
          <button onClick={() => setActiveForm('sale')}
            className="p-6 bg-white rounded-xl border-2 border-slate-200 hover:border-blue-400 hover:shadow-md text-left transition-all group">
            <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center mb-3 group-hover:bg-green-600 transition-colors">
              <DollarSign size={20} className="text-green-600 group-hover:text-white transition-colors" />
            </div>
            <h3 className="font-semibold text-slate-800 mb-1">Record a Sale</h3>
            <p className="text-slate-500 text-sm">Individual patient sale entry</p>
          </button>
        </div>
      </div>
    </div>
  );

  // EOD Form
  if (activeForm === 'eod') return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => setActiveForm(null)} className="text-slate-400 hover:text-slate-600 text-sm">← Back</button>
            <span className="text-slate-300">|</span>
            <p className="font-semibold text-slate-800 text-sm">{orgInfo?.org_name} — End of Day</p>
          </div>
        </div>
      </div>
      <div className="max-w-2xl mx-auto px-4 py-8">
        <form onSubmit={handleEodSubmit} className="space-y-5">
          <Card className="bg-white shadow-sm">
            <CardHeader className="pb-3"><CardTitle className="text-base font-heading">Date & Location</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-slate-700 text-sm font-medium">Date <span className="text-red-500">*</span></Label>
                  <Popover open={eodDateOpen} onOpenChange={setEodDateOpen}>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start text-left">
                        <CalendarIcon size={13} className="mr-2" />
                        {eodForm.submission_date ? format(new Date(eodForm.submission_date + 'T00:00:00'), 'MMMM d, yyyy') : 'Select'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-white">
                      <Calendar mode="single"
                        selected={eodForm.submission_date ? new Date(eodForm.submission_date + 'T00:00:00') : undefined}
                        onSelect={d => { if (d) { setEodField('submission_date', format(d, 'yyyy-MM-dd')); setEodDateOpen(false); } }} />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-1">
                  <Label className="text-slate-700 text-sm font-medium">Location <span className="text-red-500">*</span></Label>
                  <Select value={eodForm.location_id} onValueChange={v => setEodField('location_id', v)}>
                    <SelectTrigger><SelectValue placeholder="Select location..." /></SelectTrigger>
                    <SelectContent className="bg-white">
                      {(orgInfo?.locations || []).map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white shadow-sm">
            <CardHeader className="pb-3"><CardTitle className="text-base font-heading">Phone & Scheduling</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-3">
                <IntInput label="Inbound Calls" name="inbound_calls" value={eodForm.inbound_calls} onChange={setEodField} />
                <IntInput label="Calls Answered" name="calls_answered" value={eodForm.calls_answered} onChange={setEodField} />
                <IntInput label="Appts Scheduled" name="appointments_scheduled" value={eodForm.appointments_scheduled} onChange={setEodField} />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white shadow-sm">
            <CardHeader className="pb-3"><CardTitle className="text-base font-heading">Consult Metrics</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-3">
                <IntInput label="Consults Scheduled" name="total_consults_scheduled" value={eodForm.total_consults_scheduled} onChange={setEodField} required />
                <IntInput label="Consults Showed" name="total_consults_showed" value={eodForm.total_consults_showed} onChange={setEodField} required />
                <IntInput label="FA Presented" name="full_arch_cases_presented" value={eodForm.full_arch_cases_presented} onChange={setEodField} required />
                <IntInput label="Non-FA Presented" name="non_full_arch_cases_presented" value={eodForm.non_full_arch_cases_presented} onChange={setEodField} />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white shadow-sm" style={{ borderLeft: '3px solid hsl(191,95%,43%)' }}>
            <CardHeader className="pb-3"><CardTitle className="text-base font-heading">Today’s Outcomes</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-3">
                <IntInput label="Closed (Same Day)" name="closed_today_from_today" value={eodForm.closed_today_from_today} onChange={setEodField} required />
                <IntInput label="FA Sold (Same Day)" name="full_arch_sold_today_from_today" value={eodForm.full_arch_sold_today_from_today} onChange={setEodField} required />
                <IntInput label="Arches Sold (Same Day)" name="arches_sold_today_from_today" value={eodForm.arches_sold_today_from_today} onChange={setEodField} required />
                <IntInput label="Treatments (Same Day)" name="total_treatments_sold_today_from_today" value={eodForm.total_treatments_sold_today_from_today} onChange={setEodField} required />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white shadow-sm">
            <CardHeader className="pb-3"><CardTitle className="text-base font-heading">From Previous Consults</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-3">
                <IntInput label="Closed (Previous)" name="closed_from_previous" value={eodForm.closed_from_previous} onChange={setEodField} required />
                <IntInput label="FA Sold (Previous)" name="full_arch_sold_from_previous" value={eodForm.full_arch_sold_from_previous} onChange={setEodField} required />
                <IntInput label="Arches (Previous)" name="arches_sold_from_previous" value={eodForm.arches_sold_from_previous} onChange={setEodField} required />
                <IntInput label="Treatments (Previous)" name="total_treatments_sold_from_previous" value={eodForm.total_treatments_sold_from_previous} onChange={setEodField} required />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white shadow-sm">
            <CardHeader className="pb-3"><CardTitle className="text-base font-heading">Financial & Notes</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-sm font-medium text-slate-700">Collections Today</Label>
                  <MoneyInput value={eodForm.total_collections_today} onValueChange={v => setEodField('total_collections_today', v)} />
                </div>
                <div className="space-y-1">
                  <Label className="text-sm font-medium text-slate-700">Surgical Production</Label>
                  <MoneyInput value={eodForm.surgical_production_today} onValueChange={v => setEodField('surgical_production_today', v)} />
                </div>
              </div>
              <Textarea value={eodForm.notes} onChange={e => setEodField('notes', e.target.value)}
                placeholder="Notes..." className="min-h-[60px]" />
            </CardContent>
          </Card>
          <Button type="submit" disabled={submitting} className="w-full h-11 font-semibold"
            style={{ background: 'hsl(191,95%,43%)', color: 'hsl(222,47%,11%)' }}>
            {submitting ? <><Loader2 size={15} className="animate-spin mr-2" />Submitting...</> : 'Submit End of Day'}
          </Button>
        </form>
      </div>
    </div>
  );

  // Call Center EOD Form — all fields optional
  if (activeForm === 'callcenter') return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="max-w-2xl mx-auto flex items-center gap-3">
          <button onClick={() => setActiveForm(null)} className="text-slate-400 hover:text-slate-600 text-sm">← Back</button>
          <span className="text-slate-300">|</span>
          <p className="font-semibold text-slate-800 text-sm">{orgInfo?.org_name} — Call Center EOD</p>
        </div>
      </div>
      <div className="max-w-2xl mx-auto px-4 py-8">
        <form onSubmit={async (e) => {
          e.preventDefault();
          if (!eodForm.location_id) { toast.error('Please select a location'); return; }
          setSubmitting(true);
          try {
            await axios.post(`${API}/public/forms/${token}/eod`, {
              ...eodForm,
              form_type: 'call_center',
              inbound_calls: parseInt(eodForm.inbound_calls) || 0,
              calls_answered: parseInt(eodForm.calls_answered) || 0,
              appointments_scheduled: parseInt(eodForm.appointments_scheduled) || 0,
              total_consults_scheduled: parseInt(eodForm.total_consults_scheduled) || 0,
              total_consults_showed: parseInt(eodForm.total_consults_showed) || 0,
              full_arch_cases_presented: parseInt(eodForm.full_arch_cases_presented) || 0,
              non_full_arch_cases_presented: parseInt(eodForm.non_full_arch_cases_presented) || 0,
              total_collections_today: 0, surgical_production_today: 0,
              closed_today_from_today: 0, full_arch_sold_today_from_today: 0,
              arches_sold_today_from_today: 0, total_treatments_sold_today_from_today: 0,
              closed_from_previous: 0, full_arch_sold_from_previous: 0,
              arches_sold_from_previous: 0, non_full_arch_from_previous: 0,
              total_treatments_sold_from_previous: 0,
            });
            setSubmitted(true);
          } catch (err) { toast.error(err?.response?.data?.detail || 'Submission failed'); }
          finally { setSubmitting(false); }
        }} className="space-y-5">
          <Card className="bg-white shadow-sm">
            <CardHeader className="pb-3"><CardTitle className="text-base font-heading">Date & Location</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-slate-700 text-sm font-medium">Date <span className="text-red-500">*</span></Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start text-left">
                        <CalendarIcon size={13} className="mr-2" />
                        {eodForm.submission_date ? format(new Date(eodForm.submission_date + 'T00:00:00'), 'MMMM d, yyyy') : 'Select date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-white">
                      <Calendar mode="single" selected={eodForm.submission_date ? new Date(eodForm.submission_date + 'T00:00:00') : undefined}
                        onSelect={d => { if (d) setEodForm(p => ({ ...p, submission_date: format(d, 'yyyy-MM-dd') })); }} />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-1">
                  <Label className="text-slate-700 text-sm font-medium">Location <span className="text-red-500">*</span></Label>
                  <Select value={eodForm.location_id} onValueChange={v => setEodForm(p => ({ ...p, location_id: v }))}>
                    <SelectTrigger><SelectValue placeholder="Select location..." /></SelectTrigger>
                    <SelectContent className="bg-white">
                      {(orgInfo?.locations || []).map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-heading">Phone & Scheduling</CardTitle>
              <p className="text-xs text-slate-400 mt-0.5">All fields optional</p>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4">
                {[['Inbound Calls','inbound_calls'],['Calls Answered','calls_answered'],['Appointments Scheduled','appointments_scheduled']].map(([lbl,key]) => (
                  <IntInput key={key} label={lbl} name={key} value={eodForm[key]} onChange={(n,v) => setEodForm(p => ({ ...p, [n]: v }))} />
                ))}
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-heading">Consult Tracking <span className="text-slate-400 text-xs font-normal">(optional)</span></CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                {[['Consults Scheduled','total_consults_scheduled'],['Consults Showed','total_consults_showed'],['FA Presented','full_arch_cases_presented'],['Non-FA Presented','non_full_arch_cases_presented']].map(([lbl,key]) => (
                  <IntInput key={key} label={lbl} name={key} value={eodForm[key]} onChange={(n,v) => setEodForm(p => ({ ...p, [n]: v }))} />
                ))}
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white shadow-sm">
            <CardHeader className="pb-3"><CardTitle className="text-base font-heading">Notes</CardTitle></CardHeader>
            <CardContent>
              <Textarea value={eodForm.notes} onChange={e => setEodForm(p => ({ ...p, notes: e.target.value }))}
                placeholder="Notes..." className="min-h-[60px]" />
            </CardContent>
          </Card>
          <Button type="submit" disabled={submitting} className="w-full h-11 font-semibold">
            {submitting ? <><Loader2 size={15} className="animate-spin mr-2" />Submitting...</> : 'Submit Call Center EOD'}
          </Button>
        </form>
      </div>
    </div>
  );

  // Sale Form
  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="max-w-2xl mx-auto flex items-center gap-3">
          <button onClick={() => setActiveForm(null)} className="text-slate-400 hover:text-slate-600 text-sm">← Back</button>
          <span className="text-slate-300">|</span>
          <p className="font-semibold text-slate-800 text-sm">{orgInfo?.org_name} — Record Sale</p>
        </div>
      </div>
      <div className="max-w-2xl mx-auto px-4 py-8">
        <form onSubmit={handleSaleSubmit} className="space-y-5">
          <Card className="bg-white shadow-sm">
            <CardHeader className="pb-3"><CardTitle className="text-base font-heading">Location & Date</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-slate-700 text-sm font-medium">Location <span className="text-red-500">*</span></Label>
                  <Select value={saleForm.location_id} onValueChange={v => setSaleField('location_id', v)}>
                    <SelectTrigger><SelectValue placeholder="Select location..." /></SelectTrigger>
                    <SelectContent className="bg-white">
                      {(orgInfo?.locations || []).map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <DatePickerField label="Initial Consult Date *" fieldName="initial_consult_date" value={saleForm.initial_consult_date} onChange={setSaleField} />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white shadow-sm">
            <CardHeader className="pb-3"><CardTitle className="text-base font-heading">Patient</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-sm font-medium text-slate-700">First Name <span className="text-red-500">*</span></Label>
                  <Input value={saleForm.patient_first_name} onChange={e => setSaleField('patient_first_name', e.target.value)} placeholder="John" />
                </div>
                <div className="space-y-1">
                  <Label className="text-sm font-medium text-slate-700">Last Name <span className="text-red-500">*</span></Label>
                  <Input value={saleForm.patient_last_name} onChange={e => setSaleField('patient_last_name', e.target.value)} placeholder="Smith" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white shadow-sm" style={{ borderLeft: '3px solid hsl(191,95%,43%)' }}>
            <CardHeader className="pb-3"><CardTitle className="text-base font-heading">Treatment & Payment</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-sm font-medium text-slate-700">Treatment <span className="text-red-500">*</span></Label>
                  <Select value={saleForm.treatment_sold} onValueChange={v => setSaleField('treatment_sold', v)}>
                    <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent className="bg-white">{(orgInfo?.treatment_types || []).map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-sm font-medium text-slate-700">Total Sale Amount <span className="text-red-500">*</span></Label>
                  <MoneyInput value={saleForm.total_sales_amount} onValueChange={v => setSaleField('total_sales_amount', v)} />
                </div>
                <div className="space-y-1">
                  <Label className="text-sm font-medium text-slate-700">Payment Method <span className="text-red-500">*</span></Label>
                  <Select value={saleForm.payment_method} onValueChange={v => setSaleField('payment_method', v)}>
                    <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent className="bg-white">{(orgInfo?.payment_methods || []).map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-sm font-medium text-slate-700">Collected Today</Label>
                  <MoneyInput value={saleForm.total_collected_today} onValueChange={v => setSaleField('total_collected_today', v)} />
                </div>
                <div className="space-y-1">
                  <Label className="text-sm font-medium text-slate-700">Financing Amount</Label>
                  <MoneyInput value={saleForm.financing_amount} onValueChange={v => setSaleField('financing_amount', v)} />
                </div>
                <div className="space-y-1">
                  <Label className="text-sm font-medium text-slate-700">Referral Source <span className="text-red-500">*</span></Label>
                  <Select value={saleForm.referral_source} onValueChange={v => setSaleField('referral_source', v)}>
                    <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent className="bg-white">{(orgInfo?.referral_sources || []).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="mt-4 flex items-center gap-3">
                <Switch checked={saleForm.will_use_dental_insurance} onCheckedChange={v => setSaleField('will_use_dental_insurance', v)} />
                <Label className="text-sm text-slate-700">Will use dental insurance</Label>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white shadow-sm">
            <CardHeader className="pb-3"><CardTitle className="text-base font-heading">Timeline & Notes</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <DatePickerField label="Treatment Start Date" fieldName="treatment_start_date" value={saleForm.treatment_start_date} onChange={setSaleField} />
                <DatePickerField label="Paid In Full Date" fieldName="anticipated_paid_in_full_date" value={saleForm.anticipated_paid_in_full_date} onChange={setSaleField} />
              </div>
              <Textarea value={saleForm.notes} onChange={e => setSaleField('notes', e.target.value)} placeholder="Financial notes, case details..." className="min-h-[60px]" />
            </CardContent>
          </Card>
          <Button type="submit" disabled={submitting} className="w-full h-11 font-semibold"
            style={{ background: 'hsl(191,95%,43%)', color: 'hsl(222,47%,11%)' }}>
            {submitting ? <><Loader2 size={15} className="animate-spin mr-2" />Submitting...</> : 'Record Sale'}
          </Button>
        </form>
      </div>
    </div>
  );
}
